import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../lib/auth'

// The change history of one record, from the activity log (complete
// accountability, migration 076). Owner + admin only - renders nothing for
// anyone else (the API refuses them too). Nothing exists before 2026-10-25.

export interface HistoryRow {
  id: number
  at: string
  actor: string
  viewed_as_by: string | null
  action: 'INSERT' | 'UPDATE' | 'DELETE' | 'TRUNCATE'
  details: Record<string, unknown> | null
  via_action: string | null
  via_status: number | null
  via_run: number | null
  via_script: string | null
}

export function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

export function fmtValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '(empty)'
  if (typeof v === 'string') return v.length > 160 ? v.slice(0, 160) + '…' : v
  const s = JSON.stringify(v)
  return s.length > 160 ? s.slice(0, 160) + '…' : s
}

/** UPDATE details are {field: [before, after]}; INSERT / DELETE details are the whole row. */
export function ChangeList({ action, details }: { action: string; details: Record<string, unknown> | null }) {
  if (!details) return null
  const entries = Object.entries(details)
  if (action === 'UPDATE') {
    return (
      <ul className="mt-1 space-y-0.5">
        {entries.map(([k, v]) => {
          const [before, after] = Array.isArray(v) ? v : [undefined, v]
          return (
            <li key={k} className="text-[11.5px] text-ink-700 break-words">
              <span className="font-medium">{k}</span>:{' '}
              <span className="text-ink-400 line-through decoration-ink-300">{fmtValue(before)}</span>
              {' → '}<span>{fmtValue(after)}</span>
            </li>
          )
        })}
      </ul>
    )
  }
  return (
    <details className="mt-1">
      <summary className="text-[11px] text-ink-500 cursor-pointer">{entries.length} fields</summary>
      <ul className="mt-1 space-y-0.5">
        {entries.map(([k, v]) => (
          <li key={k} className="text-[11px] text-ink-600 break-words"><span className="font-medium">{k}</span>: {fmtValue(v)}</li>
        ))}
      </ul>
    </details>
  )
}

const VERB: Record<string, string> = { INSERT: 'Created', UPDATE: 'Changed', DELETE: 'Deleted', TRUNCATE: 'Table emptied' }

export function RecordHistory({ entity, id, defaultOpen = false }: { entity: string; id: string | number; defaultOpen?: boolean }) {
  const { isAdmin } = useAuth()
  const [open, setOpen] = useState(defaultOpen)
  const { data, isLoading, error } = useQuery<{ rows: HistoryRow[] }>({
    queryKey: ['record-history', entity, String(id)],
    queryFn: async () => {
      const r = await fetch(`/api/activity/record?entity=${encodeURIComponent(entity)}&id=${encodeURIComponent(String(id))}`)
      if (!r.ok) throw new Error((await r.json().catch(() => null))?.error || `HTTP ${r.status}`)
      return r.json()
    },
    enabled: isAdmin && open,
  })
  if (!isAdmin) return null

  return (
    <div>
      <button onClick={() => setOpen(o => !o)}
              className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2 hover:text-ink-700">
        {open ? '▾' : '▸'} History · who changed what
      </button>
      {open && (
        isLoading ? <p className="text-[11.5px] text-ink-400">Loading…</p>
        : error ? <p className="text-[11.5px] text-flame-600">{(error as Error).message}</p>
        : !data?.rows.length ? (
          <p className="text-[11.5px] text-ink-500 bg-ink-50 rounded-md p-2">
            No changes recorded. The activity log starts on 25 Oct 2026; earlier changes have no record.
          </p>
        ) : (
          <ol className="space-y-2">
            {data.rows.map(h => (
              <li key={h.id} className="border-l-2 border-ink-200 pl-2.5">
                <div className="text-[11.5px] text-ink-800">
                  <span className="font-medium">{VERB[h.action] ?? h.action}</span>
                  {' by '}<span className="font-medium">{h.actor}</span>
                  {h.viewed_as_by && <span className="text-ink-500"> (owner {h.viewed_as_by} viewing as)</span>}
                </div>
                <div className="text-[10.5px] text-ink-400">
                  {fmtWhen(h.at)}
                  {h.via_action && <> · {h.via_action}</>}
                  {h.via_run != null && <> · script run #{h.via_run} {h.via_script}</>}
                </div>
                <ChangeList action={h.action} details={h.details} />
              </li>
            ))}
          </ol>
        )
      )}
    </div>
  )
}
